import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ChangeBorrowerAssignment } from 'app/store/borrower/borrower/borrower.actions';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { Store } from '@ngxs/store';
import { Borrower } from 'app/models/dto/borrower.model';
import { LenderUserService } from 'app/services/lender-user/lender-user.service';
import { BorrowerState } from 'app/store/borrower/borrower/borrower.state';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UpdateDealDetails } from 'ng2-app/store/deal/deal/deal.actions';
import { DealState } from 'ng2-app/store/deal/deal/deal.state';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventBusService } from 'ng2-app/modules/shared/services/event-bus.service';

interface TeamMemberGrouped {
  teamName: string;
  teamMembers: { value: number, label: string }[];
}


@Component({
  selector: 'app-change-assignment-smart',
  templateUrl: './change-assignment-smart.component.html',
  styleUrls: ['./change-assignment-smart.component.scss'],
  standalone: false
})
export class ChangeAssignmentSmartComponent implements OnInit {
  //@ts-ignore
  borrower$: Observable<Borrower> = this.store.select( BorrowerState.borrower );
  borrowerSaving$: Observable<boolean> = this.store.select( BorrowerState.saving );
  dealSaving$: Observable<boolean> = this.store.select( DealState.savingDeal );
  saving$: Observable<boolean>;

  form: UntypedFormGroup;
  model: { teamMemberId: number };
  fields: FormlyFieldConfig[];
  noMembersFound = false;

  constructor(
    private dialogRef: MatDialogRef<ChangeAssignmentSmartComponent>,
    private lenderUserService: LenderUserService,
    private store: Store,
    private eventBusService: EventBusService,
    @Inject(MAT_DIALOG_DATA) public data,
  ) {
    this.saving$ = combineLatest([this.borrowerSaving$, this.dealSaving$]).pipe(
      takeUntilDestroyed(),
      map(([borrowerSaving, dealSaving]) => this.data?.type === 'deal' ? dealSaving : borrowerSaving),
    );
    this.eventBusService.subscribe( this.eventBusService.types.AssignmentChangeCompletedEvent ).pipe(
      takeUntilDestroyed()
    ).subscribe(() => {
      this.dialogRef.close();
    });
  }

  ngOnInit(): void {
    this.initForm();
  }

  save() {
    const lenderUserId = this.model.teamMemberId;
    return this.data?.type === 'deal' ? this.saveDeal(lenderUserId) : this.saveBorrower(lenderUserId);
  }

  saveBorrower(lenderUserId: number) {
    this.borrower$.pipe(
      take(1)
    ).subscribe( borrower => {
      this.store.dispatch(new ChangeBorrowerAssignment(borrower.id, lenderUserId));
    });
  }

  saveDeal(lenderUserId: number) {
    const deal = this.data?.deal;
    if(!deal?.id) {
      return this.dialogRef.close();
    }
    this.store.dispatch( new UpdateDealDetails(deal.id, { lenderUserId }, true) );
  }

  private getGroupedTeamMembers(teamMembers) {
    const grouped = new Array<TeamMemberGrouped>();
    teamMembers.forEach(member => {
      const found = grouped.find(el => el.teamName === member.team_lender_user.team.name);
      const newTeamMember = { value: member.id, label: member.fullName };
      if (found) {
        found['teamMembers'].push(newTeamMember);
      } else {
        grouped.push({ 'teamName': member.team_lender_user.team.name, 'teamMembers': [newTeamMember] });
      }
    });
    return grouped;
  }


  private getTeamMembers(search: string): Observable<TeamMemberGrouped[] | Observable<never[]>> {
    if (search.length < 2) {
      return of([]);
    }
    return this.lenderUserService.search(search).pipe(
      map(teamMembers => {
        if (teamMembers.length === 0) {
          this.noMembersFound = true;
        } else {
          this.noMembersFound = false;
        }
        return this.getGroupedTeamMembers(teamMembers);
      })
    );
  }

  private initForm() {
    this.form = new UntypedFormGroup({});
    this.model = { teamMemberId: 0 };

    this.fields = [
      {
        key: 'teamMemberId',
        type: 'autocomplete',
        templateOptions: {
          label: 'Search team members',
          attributes: { 'data-cy': 'formly-autocomplete' },
          getOptions: search => this.getTeamMembers(search),
          hasSubGroup: true,
          required: true
        }
      }
    ];
  }

}
